"use client";

import { useState } from "react";

interface FAQItem {
	id: string;
	question: string;
	answer: string;
}

const faqData: FAQItem[] = [
	{
		id: "chatgpt-vs-koach",
		question: "How is this different from just asking ChatGPT or Claude?",
		answer:
			"You could absolutely ask ChatGPT about running. But you'd need to explain your entire situation every single time: your training history, current mileage, recent workouts, HRV, sleep, goals, injuries. Then hope it remembers context from your last conversation (it probably won't).\n\nWe automatically know all of this. Your Garmin data, your Strava runs, your recovery metrics—everything flows in seamlessly. Ask a simple question, get advice that's actually personalized to you. No copy-pasting training logs. No repeating yourself.",
	},
	{
		id: "human-coach",
		question: "Can't I just hire a human coach instead?",
		answer:
			"If you can afford $100–300/month and want structured programming with regular check-ins, absolutely go for it. Human coaches are incredible.\n\nBut most runners don't need that level of commitment. Maybe you just want someone to bounce ideas off. Or you need quick advice at 6 AM before a run. Or you're happy with your own plan but occasionally want a second opinion.\n\nThat's us. The running buddy who's always available, knows your context, and costs 90% less.",
	},
	{
		id: "injury-prevention",
		question: "Will this actually help me avoid injuries?",
		answer:
			"We're not doctors, and we can't diagnose injuries. But we can help you make smarter training decisions.\n\nFeeling unusually tired with low HRV? We might suggest scaling back. Ramping up mileage too fast? We'll flag it. Ignoring recovery signals? We'll remind you.\n\nThink of it as a safety net that knows your patterns and can spot risky trends before they become problems.",
	},
	{
		id: "training-plan",
		question: "Can it create a full training plan for me?",
		answer:
			"Not yet, but it's coming.\n\nRight now, we're focused on conversational advice: answering your questions, helping you adjust workouts, thinking through decisions.\n\nDown the road, we'll add structured plan generation that exports directly to your device. But we're starting with what runners actually need most: someone to talk to.",
	},
	{
		id: "advice-quality",
		question: "Is the advice actually good, or just generic AI responses?",
		answer:
			"The AI is specialized in running coaching—not generic chat. It understands training principles, periodization, injury prevention, pacing strategies, and sports psychology.\n\nMore importantly, it knows YOUR context. Your goals, your history, your current fitness, your life circumstances. So the advice isn't just technically sound—it's relevant to you specifically.\n\nThat said, we're always improving. Early users will help us get even better.",
	},
	{
		id: "experience-level",
		question: "Do I need to be an experienced runner to use this?",
		answer:
			"Nope. Whether you're on week 2 of Couch to 5K or training for your 10th marathon, we adapt to your level.\n\nBeginners get explanations and encouragement. Experienced runners get nuanced advice and performance optimization. The AI meets you where you are.",
	},
	{
		id: "trust-ai",
		question: "Why should I trust an AI with my training?",
		answer:
			"You shouldn't trust blindly. And we'd never ask you to.\n\nUse us as a thinking partner. Get a second opinion. Validate your instincts. Challenge our advice. That's how good training decisions get made—through informed discussion, not blind obedience.\n\nWe're here to help you think through your training, not replace your judgment.",
	},
	{
		id: "non-running-questions",
		question: "What if my question isn't about running?",
		answer:
			"Then we probably can't help. We're specialized in running coaching—training, injuries, pacing, recovery, race strategy, that kind of thing.\n\nWe're not nutritionists, physical therapists, or general life coaches. We know our lane, and we stay in it.",
	},
	{
		id: "get-started",
		question: "How do I get started?",
		answer:
			"Join the waitlist. We'll email you when we're ready for more users.\n\nEarly access means you help shape the product. Your feedback matters. Plus, you'll lock in the $10/month founding member price before we (eventually) raise it.",
	},
];

function ChevronDownIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			role="img"
			aria-label="Toggle FAQ item"
		>
			<title>Toggle FAQ item</title>
			<path
				d="m6 9 6 6 6-6"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

export default function FAQSection() {
	const [openItems, setOpenItems] = useState<string[]>([]);

	const toggleItem = (id: string) => {
		setOpenItems((prev) =>
			prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
		);
	};

	return (
		<div className="w-full border-b border-[rgba(55,50,47,0.12)] flex justify-center items-start">
			<div className="flex-1 px-4 md:px-12 py-16 md:py-20 flex flex-col lg:flex-row justify-start items-start gap-6 lg:gap-12">
				{/* Left Column - Header */}
				<div className="w-full lg:flex-1 flex flex-col justify-center items-start gap-4 lg:py-5">
					<div className="w-full flex flex-col justify-center text-[#49423D] font-semibold leading-tight md:leading-[44px] font-sans text-4xl tracking-tight">
						Frequently Asked Questions
					</div>
					<div className="w-full text-[#605A57] text-base font-normal leading-7 font-sans">
						What you're actually wondering, answered straight.
					</div>
				</div>

				{/* Right Column - FAQ Items */}
				<div className="w-full lg:flex-1 flex flex-col justify-center items-center">
					<div className="w-full flex flex-col">
						{faqData.map((item) => {
							const isOpen = openItems.includes(item.id);

							return (
								<div
									key={item.id}
									className="w-full border-b border-[rgba(73,66,61,0.16)] overflow-hidden"
								>
									<button
										type="button"
										onClick={() => toggleItem(item.id)}
										className="w-full px-5 py-[18px] flex justify-between items-center gap-5 text-left hover:bg-[rgba(73,66,61,0.02)] transition-colors duration-200"
										aria-expanded={isOpen}
									>
										<div className="flex-1 text-[#49423D] text-base font-medium leading-6 font-sans">
											{item.question}
										</div>
										<div className="flex justify-center items-center">
											<ChevronDownIcon
												className={`w-6 h-6 text-[rgba(73,66,61,0.60)] transition-transform duration-300 ease-in-out ${
													isOpen ? "rotate-180" : "rotate-0"
												}`}
											/>
										</div>
									</button>

									<div
										className={`overflow-hidden transition-all duration-300 ease-in-out ${
											isOpen ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
										}`}
									>
										<div className="px-5 pb-[18px] text-[#605A57] text-sm font-normal leading-6 font-sans whitespace-pre-line">
											{item.answer}
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
}
